/**
 * POVTemplate — One-sided pressure
 * ---------------------------------------------------------------
 * Only "them" speaks. Slow camera push, ambient compression dim,
 * resonant phrases isolate the field. Forensic end-frame closes.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import type { SimulationContent } from "@/lib/contentGenerator";
import { planTimeline, PALETTE, inResonantWindow } from "../utils/timing";
import { BackgroundField } from "../components/BackgroundField";
import { MessageBubble } from "../components/MessageBubble";
import { SpiralOverlay } from "../components/SpiralOverlay";
import { CTAFrame } from "../components/CTAFrame";

export const POVTemplate: React.FC<{ sim: SimulationContent }> = ({ sim }) => {
  const plan = planTimeline(sim);
  const frame = useCurrentFrame();
  const isolation = inResonantWindow(frame, plan.resonantFrames);

  const push = interpolate(frame, [0, plan.ctaStartFrame], [1.0, 1.04], {
    extrapolateRight: "clamp",
  });
  const dim = interpolate(frame, [0, plan.ctaStartFrame], [0, 0.22], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bg }}>
      <BackgroundField isolate={isolation.intensity} />

      <Sequence durationInFrames={plan.ctaStartFrame}>
        <AbsoluteFill
          style={{
            padding: "200px 80px 160px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            transform: `scale(${push})`,
            transformOrigin: "50% 60%",
          }}
        >
          {plan.turns.map((t) => {
            const visible = frame >= t.startFrame;
            const newestIdx = plan.turns.reduce(
              (acc, x, idx) => (frame >= x.startFrame ? idx : acc),
              -1
            );
            const idx = plan.turns.findIndex((x) => x.index === t.index);
            const stackOffset = Math.max(0, newestIdx - idx);
            const isFocal =
              isolation.active && t.resonant && Math.abs(t.revealFrame - isolation.anchor) < 4;
            return (
              <MessageBubble
                key={t.index}
                turn={t}
                visible={visible}
                stackOffset={stackOffset}
                isolationActive={isolation.active}
                isFocal={isFocal}
              />
            );
          })}
        </AbsoluteFill>

        {/* Compression vignette grows over time */}
        <AbsoluteFill
          style={{
            background: `radial-gradient(circle at 50% 90%, rgba(0,0,0,${dim}) 0%, transparent 60%)`,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 130,
            left: 60,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 18,
            letterSpacing: "0.42em",
            color: PALETTE.cyanSoft,
            textTransform: "uppercase",
            opacity: 0.75,
          }}
        >
          POV · You haven't said a word
        </div>

        <SpiralOverlay spiralFrame={plan.spiralFrame} />
      </Sequence>

      <Sequence from={plan.ctaStartFrame} durationInFrames={plan.ctaFrames}>
        <CTAFrame preset="archived" />
      </Sequence>
    </AbsoluteFill>
  );
};
